import axios, { AxiosInstance } from 'axios';
import { getAuthToken } from '@/utils/token';

// Create axios instance with base config - using env variable directly for production compatibility
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to construct calendar API URLs
const getCalendarUrl = (endpoint: string): string => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api';
  const cleanBaseUrl = baseUrl.endsWith('/') 
    ? baseUrl.slice(0, -1) 
    : baseUrl;
    
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  
  // Don't add /api again since baseURL already includes it
  return `${cleanBaseUrl}/calendar/${cleanEndpoint}`;
};

export interface CalendarEvent {
  title: string;
  description?: string;
  startTime: string; // ISO string
  endTime: string;   // ISO string
  location?: string;
  attendees?: string[];
}

export interface CalendarStatus {
  connected: boolean;
}

export interface CalendarAuthUrl {
  authUrl: string;
}

export interface CalendarEventResponse {
  eventId: string;
  message: string;
}

interface CalendarApi {
  getStatus: () => Promise<CalendarStatus>;
  getAuthUrl: () => Promise<CalendarAuthUrl>;
  connect: (code: string) => Promise<{ message: string }>;
  disconnect: () => Promise<{ message: string }>;
  createEvent: (event: CalendarEvent) => Promise<CalendarEventResponse>;
  deleteEvent: (eventId: string) => Promise<{ message: string }>;
}

const calendarApi: CalendarApi = {
  // Get calendar connection status
  async getStatus(): Promise<CalendarStatus> {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await api.get<CalendarStatus>(
        getCalendarUrl('/status'),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Get calendar status error:', error);
      throw error;
    }
  },

  // Get Google OAuth authorization URL
  async getAuthUrl(): Promise<CalendarAuthUrl> {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await api.get<CalendarAuthUrl>(
        getCalendarUrl('/auth-url'),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Get auth URL error:', error);
      throw error;
    }
  },

  // Connect Google Calendar with authorization code
  async connect(code: string): Promise<{ message: string }> {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await api.post<{ message: string }>(
        getCalendarUrl('/connect'),
        { code },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Connect calendar error:', error);
      throw error;
    }
  },

  // Disconnect Google Calendar
  async disconnect(): Promise<{ message: string }> {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await api.delete<{ message: string }>(
        getCalendarUrl('/disconnect'),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Disconnect calendar error:', error);
      throw error;
    }
  },

  // Create calendar event
  async createEvent(event: CalendarEvent): Promise<CalendarEventResponse> {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await api.post<CalendarEventResponse>(
        getCalendarUrl('/events'),
        event,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Create calendar event error:', error);
      throw error;
    }
  },

  // Delete calendar event
  async deleteEvent(eventId: string): Promise<{ message: string }> {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await api.delete<{ message: string }>(
        getCalendarUrl(`/events/${eventId}`),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Delete calendar event error:', error);
      throw error;
    }
  },
};

export { calendarApi };
export default calendarApi;
